import { useState } from 'react';
import { AirplaneTakeoffIcon } from '@phosphor-icons/react';
import './airline_logo.css';

/** Tamanho único do logo da companhia (`AirlineLogo`) em TODAS as superfícies de
 *  voo — ofertas, seletor de tarifa, trechos dos cards, recibo "Sua reserva" e a
 *  vista "Detalhe do voo". Padroniza o glifo entre as telas. */
export const AIRLINE_MARK_SIZE = 22;

/**
 * Logo da companhia aérea — o thumb quadrado, servido por IATA.
 *
 * **`al_square` e nada mais.** O `pics.avs.io` tem duas variantes: a sem sufixo
 * devolve o LOCKUP HORIZONTAL da companhia, e num slot quadrado ele é letterboxado
 * até desaparecer (a LATAM cai pra ~6px de altura; a American, mais deitada ainda,
 * some). A logo é o único elemento do card que a pessoa reconhece ANTES de ler —
 * marca de 6px não reconhece nada. `al_square` é a marca já enquadrada. Vale a
 * troca: quadrado colorido custa um pouco de sobriedade, slot incontrolável custa
 * a função.
 *
 * Pede-se o DOBRO do tamanho de exibição por causa de tela retina.
 */
function airlineLogoUrl(iata: string, px: number) {
  const side = String(px);
  return `https://pics.avs.io/al_square/${side}/${side}/${iata}.png`;
}

/**
 * A variante HORIZONTAL — o lockup completo da companhia, para o slot em que a
 * largura é livre e a altura é que manda: o cabeçalho do bilhete no documento de
 * voucher, onde a marca fecha a linha do eyebrow.
 *
 * Não substitui o quadrado: num slot quadrado este lockup é letterboxado até
 * desaparecer, que é exatamente o motivo de o `al_square` existir. São dois slots
 * diferentes, e é o CHAMADOR que sabe qual tem.
 */
function airlineLockupUrl(iata: string, px: number) {
  // A rota RAIZ do CDN é a horizontal; `al_square` é o desvio. `al/<w>/<h>` não
  // existe — devolve 404 e cai no chip do IATA (medido).
  return `https://pics.avs.io/${String(px * 4)}/${String(px)}/${iata}.png`;
}

/**
 * O glifo da companhia em TODAS as superfícies de voo — ofertas, trechos dos
 * cards, recibo "Sua reserva" e o cabeçalho do drawer "Detalhe do voo".
 *
 * Três estados, em ordem de preferência: o thumb da companhia; o IATA num chip
 * neutro quando o CDN não tem aquela companhia (404 real → o `onError` dispara);
 * o avião genérico quando não há nem IATA. Logo que não carrega não pode virar
 * buraco no layout — o chip ocupa o mesmo espaço e ainda informa.
 */
export function AirlineLogo({
  iata,
  name,
  size,
  shape = 'square',
}: {
  readonly iata?: string;
  readonly name?: string;
  /** No `square` é o LADO; no `wide` é a ALTURA (a largura sai da marca). */
  readonly size: number;
  readonly shape?: 'square' | 'wide';
}) {
  // Guarda QUAL cia falhou, não um "falhou" solto. Num slot de posição estável que
  // troca de companhia (React reusa a instância, o estado sobrevive), o booleano
  // condenaria a cia seguinte ao chip sem nunca tentar a logo dela. Hoje nenhum
  // consumidor faz isso — as listas são keyed por `offerId` e os drawers montam por
  // condição —, mas a comparação custa o mesmo e não deixa a armadilha armada.
  const [failedIata, setFailedIata] = useState<string | null>(null);

  if (!iata) {
    return (
      <span className="al-logo al-logo--generic" style={{ width: size, height: size }}>
        <AirplaneTakeoffIcon size={size} aria-hidden />
      </span>
    );
  }

  if (failedIata === iata) {
    return (
      <span
        className="al-logo al-logo--chip"
        // Corpo do texto derivado da CAIXA (0,375 = os 9px calibrados no slot de
        // 24px do desenho), não do texto do pai — o chip vive dentro de cabeçalhos
        // de 12px, onde um `em` no CSS encolheria o IATA a 4px.
        style={{ width: size, height: size, fontSize: Math.round(size * 0.375) }}
      >
        {iata}
      </span>
    );
  }

  // Pede-se o DOBRO do tamanho de exibição por causa de tela retina.
  return (
    <img
      className={shape === 'wide' ? 'al-logo al-logo--wide' : 'al-logo'}
      style={shape === 'wide' ? { height: size } : { width: size, height: size }}
      src={shape === 'wide' ? airlineLockupUrl(iata, size * 2) : airlineLogoUrl(iata, size * 2)}
      alt={name ?? iata}
      onError={() => {
        setFailedIata(iata);
      }}
    />
  );
}
